import { useState } from 'react'
import { directionsData, Direction, Category, Product } from '../../data/products'
import { 
  EducationIcon, 
  CodeIcon, 
  RobotIcon,
  ChevronRightIcon,
  ChevronLeftIcon
} from '../Icons'

interface MainScreenProps {
  onLog: (message: string) => void
  onAddToCart: (product: Product) => void
  onNavigate: (screen: 'home' | 'orders' | 'profile') => void
}

type ScreenVersion = 'v1' | 'v2' | 'v3' | 'v4'

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  blue: '#3b82f6',
  red: '#ef4444',
}

export default function MainScreen({ onLog, onAddToCart }: MainScreenProps) {
  const [selectedDirection, setSelectedDirection] = useState<Direction | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [currentVersion, setCurrentVersion] = useState<ScreenVersion>('v1')

  const versions = [
    { id: 'v1' as ScreenVersion, label: 'V1', description: 'Классик' },
    { id: 'v2' as ScreenVersion, label: 'V2', description: 'Компакт' },
    { id: 'v3' as ScreenVersion, label: 'V3', description: 'Свайп А' },
    { id: 'v4' as ScreenVersion, label: 'V4', description: 'Свайп Б' },
  ]

  const getDirectionIcon = (directionId: string, size: number = 56) => {
    switch (directionId) {
      case 'education':
        return <EducationIcon size={size} color={colors.blue} />
      case 'it':
        return <CodeIcon size={size} color={colors.accent} />
      case 'robotics':
        return <RobotIcon size={size} color={colors.green} />
      default:
        return null
    }
  }

  const handleDirectionClick = (direction: Direction) => {
    setSelectedDirection(direction)
    onLog(`Открыто направление: ${direction.title}`)
  }

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category)
    onLog(`Открыта категория: ${category.title}`)
  }

  const handleProductSelect = (product: Product) => {
    onAddToCart(product)
    onLog(`Добавлен в заказы: ${product.title}`)
  }

  const handleBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null)
      onLog('← Возврат к категориям')
    } else if (selectedDirection) {
      setSelectedDirection(null)
      onLog('← Возврат на главную')
    }
  }

  // Главный экран с версиями
  if (!selectedDirection) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        paddingBottom: '100px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
      }}>
        {/* Переключатель версий */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: colors.bg,
          borderBottom: `1px solid ${colors.cardBorder}`,
          padding: '12px 16px',
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
          }}>
            {versions.map(version => (
              <button
                key={version.id}
                onClick={() => {
                  setCurrentVersion(version.id)
                  onLog(`Переключено на: ${version.label}`)
                }}
                style={{
                  flex: '1 0 auto',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  border: currentVersion === version.id ? 'none' : `1px solid ${colors.cardBorder}`,
                  background: currentVersion === version.id
                    ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`
                    : 'transparent',
                  color: currentVersion === version.id ? '#fff' : colors.textMuted,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div>{version.label}</div>
                <div style={{
                  fontSize: '10px',
                  opacity: currentVersion === version.id ? 1 : 0.5,
                  marginTop: '2px',
                }}>
                  {version.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Рендерим версию */}
        {currentVersion === 'v1' && <DirectionsV1 onDirectionClick={handleDirectionClick} onLog={onLog} getDirectionIcon={getDirectionIcon} />}
        {currentVersion === 'v2' && <DirectionsV2 onDirectionClick={handleDirectionClick} onLog={onLog} getDirectionIcon={getDirectionIcon} />}
        {currentVersion === 'v3' && <DirectionsV3 onDirectionClick={handleDirectionClick} onLog={onLog} getDirectionIcon={getDirectionIcon} />}
        {currentVersion === 'v4' && <DirectionsV4 onDirectionClick={handleDirectionClick} onLog={onLog} getDirectionIcon={getDirectionIcon} />}
      </div>
    )
  }

  // Экран направления (категории)
  if (selectedDirection && !selectedCategory) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        paddingBottom: '100px',
      }}>
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}>
          <button
            onClick={handleBack}
            style={{
              background: 'transparent',
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: '8px',
              padding: '8px 14px',
              color: colors.text,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '16px',
            }}
          >
            <ChevronLeftIcon size={16} />
            Назад
          </button>
          <div style={{
            color: colors.text,
            fontSize: '28px',
            fontWeight: 700,
          }}>
            {selectedDirection.title}
          </div>
        </div>

        <div style={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          {selectedDirection.categories.map(category => (
            <div
              key={category.id}
              onClick={() => handleCategoryClick(category)}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: '12px',
                padding: '16px',
                cursor: 'pointer',
              }}
            >
              <div style={{
                color: colors.text,
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '4px',
              }}>
                {category.title}
              </div>
              <div style={{
                color: colors.textMuted,
                fontSize: '13px',
              }}>
                {category.products.length} товаров
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Экран товаров
  if (selectedCategory) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        paddingBottom: '100px',
      }}>
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}>
          <button
            onClick={handleBack}
            style={{
              background: 'transparent',
              border: `1px solid ${colors.cardBorder}`,
              borderRadius: '8px',
              padding: '8px 14px',
              color: colors.text,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '16px',
            }}
          >
            <ChevronLeftIcon size={16} />
            Назад
          </button>
          <div style={{
            color: colors.text,
            fontSize: '24px',
            fontWeight: 700,
          }}>
            {selectedCategory.title}
          </div>
        </div>

        <div style={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
          {selectedCategory.products.map(product => (
            <div
              key={product.id}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`,
                borderRadius: '12px',
                padding: '16px',
              }}
            >
              <div style={{
                color: colors.text,
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '8px',
              }}>
                {product.title}
              </div>
              <div style={{
                color: colors.textMuted,
                fontSize: '13px',
                marginBottom: '12px',
              }}>
                {product.description}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{
                  color: colors.accentLight,
                  fontSize: '20px',
                  fontWeight: 700,
                }}>
                  {product.price}
                </div>
                <button
                  onClick={() => handleProductSelect(product)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Купить
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return null
}

// VERSION 1: Классические стеклянные карточки (как на скриншоте)
function DirectionsV1({ onDirectionClick, getDirectionIcon }: any) {
  return (
    <>
      <div style={{
        background: `linear-gradient(180deg, ${colors.accent}20 0%, transparent 100%)`,
        padding: '40px 20px',
        marginBottom: '20px',
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '16px',
        }}>
          <div style={{
            color: colors.text,
            fontSize: '32px',
            fontWeight: 700,
            marginBottom: '8px',
          }}>
            AiVinity
          </div>
          <div style={{
            color: colors.textMuted,
            fontSize: '15px',
            lineHeight: '1.5',
          }}>
            Экосистема цифровых решений
          </div>
        </div>
      </div>

      <div style={{
        padding: '0 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}>
        {directionsData.map(dir => (
          <div
            key={dir.id}
            onClick={() => onDirectionClick(dir)}
            style={{
              background: `linear-gradient(135deg, ${dir.color}15, ${colors.cardBg})`,
              border: `1px solid ${dir.color}30`,
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              backdropFilter: 'blur(10px)',
            }}
          >
            <div style={{
              position: 'absolute',
              right: '-20px',
              bottom: '-20px',
              opacity: 0.1,
            }}>
              {getDirectionIcon(dir.id, 120)}
            </div>

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: '12px' }}>
                {getDirectionIcon(dir.id, 40)}
              </div>
              
              <div style={{
                color: colors.text,
                fontSize: '22px',
                fontWeight: 700,
                marginBottom: '8px',
              }}>
                {dir.title}
              </div>
              
              <div style={{
                color: colors.textMuted,
                fontSize: '14px',
                marginBottom: '16px',
              }}>
                {dir.categories.length} категорий • {dir.categories.reduce((sum, cat) => sum + cat.products.length, 0)} продуктов
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                color: dir.color,
                fontSize: '14px',
                fontWeight: 600,
              }}>
                Смотреть <ChevronRightIcon size={16} color={dir.color} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

// VERSION 2: Компактные карточки
function DirectionsV2({ onDirectionClick, getDirectionIcon }: any) {
  return (
    <>
      <div style={{
        padding: '20px',
        background: `linear-gradient(180deg, ${colors.accent}15 0%, transparent 100%)`,
      }}>
        <div style={{
          color: colors.text,
          fontSize: '24px',
          fontWeight: 700,
          marginBottom: '4px',
        }}>
          AiVinity
        </div>
        <div style={{
          color: colors.textMuted,
          fontSize: '13px',
        }}>
          Выберите направление
        </div>
      </div>

      <div style={{
        padding: '0 20px',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '12px',
      }}>
        {directionsData.map(dir => (
          <div
            key={dir.id}
            onClick={() => onDirectionClick(dir)}
            style={{
              background: colors.cardBg,
              border: `1px solid ${dir.color}40`,
              borderRadius: '12px',
              padding: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: `${dir.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {getDirectionIcon(dir.id, 32)}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{
                color: colors.text,
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '4px',
              }}>
                {dir.title}
              </div>
              <div style={{
                color: colors.textMuted,
                fontSize: '12px',
              }}>
                {dir.categories.length} категорий
              </div>
            </div>

            <ChevronRightIcon size={20} color={colors.textMuted} />
          </div>
        ))}
      </div>
    </>
  )
}

// VERSION 3: Свайп карусель (стиль А)
function DirectionsV3({ onDirectionClick, onLog }: any) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [startX, setStartX] = useState(0)

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const endX = e.changedTouches[0].clientX
    const diff = startX - endX

    if (diff > 50 && currentIndex < directionsData.length - 1) {
      setCurrentIndex(prev => prev + 1)
      onLog(`Свайп → направление ${currentIndex + 2}`)
    } else if (diff < -50 && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
      onLog(`Свайп ← направление ${currentIndex}`)
    }
  }

  return (
    <div 
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div style={{
        padding: '20px',
        textAlign: 'center',
      }}>
        <div style={{
          color: colors.text,
          fontSize: '28px',
          fontWeight: 700,
          marginBottom: '8px',
        }}>
          AiVinity
        </div>
        <div style={{
          color: colors.textMuted,
          fontSize: '13px',
          marginBottom: '16px',
        }}>
          Свайп влево/вправо ←→
        </div>

        <div style={{
          display: 'flex',
          gap: '6px',
          justifyContent: 'center',
        }}>
          {directionsData.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: idx === currentIndex ? colors.accent : colors.cardBorder,
                transition: 'all 0.3s',
              }}
            />
          ))}
        </div>
      </div>

      <div style={{
        padding: '0 20px',
        minHeight: '400px',
      }}>
        {directionsData.map((dir, index) => (
          <div
            key={dir.id}
            style={{
              display: index === currentIndex ? 'block' : 'none',
              background: `linear-gradient(135deg, ${dir.color}20, ${colors.cardBg})`,
              border: `2px solid ${dir.color}50`,
              borderRadius: '20px',
              padding: '32px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-40px',
              right: '-40px',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: `${dir.color}10`,
              filter: 'blur(60px)',
            }} />

            <div style={{
              fontSize: '80px',
              marginBottom: '20px',
            }}>
              {index === 0 ? '🎓' : index === 1 ? '💻' : '🤖'}
            </div>

            <div style={{
              color: colors.text,
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '12px',
            }}>
              {dir.title}
            </div>

            <div style={{
              color: colors.textMuted,
              fontSize: '14px',
              marginBottom: '24px',
            }}>
              {dir.categories.length} категорий • {dir.categories.reduce((sum, cat) => sum + cat.products.length, 0)} товаров
            </div>

            <button
              onClick={() => onDirectionClick(dir)}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
                color: '#fff',
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: `0 8px 24px ${colors.accent}40`,
              }}
            >
              Смотреть все
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// VERSION 4: Свайп карусель (стиль Б - горизонтальный скролл)
function DirectionsV4({ onDirectionClick, getDirectionIcon }: any) {
  return (
    <>
      <div style={{
        padding: '20px 20px 16px',
      }}>
        <div style={{
          color: colors.text,
          fontSize: '24px',
          fontWeight: 700,
          marginBottom: '4px',
        }}>
          AiVinity
        </div>
        <div style={{
          color: colors.textMuted,
          fontSize: '13px',
        }}>
          Свайп горизонтально →
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '16px',
        padding: '0 20px 20px',
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
      }}>
        {directionsData.map((dir) => (
          <div
            key={dir.id}
            onClick={() => onDirectionClick(dir)}
            style={{
              minWidth: '280px',
              background: `linear-gradient(135deg, ${dir.color}15, ${colors.cardBg})`,
              border: `1px solid ${dir.color}40`,
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              scrollSnapAlign: 'start',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              right: '-30px',
              top: '-30px',
              opacity: 0.15,
            }}>
              {getDirectionIcon(dir.id, 100)}
            </div>

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ marginBottom: '16px' }}>
                {getDirectionIcon(dir.id, 48)}
              </div>

              <div style={{
                color: colors.text,
                fontSize: '20px',
                fontWeight: 700,
                marginBottom: '8px',
              }}>
                {dir.title}
              </div>

              <div style={{
                color: colors.textMuted,
                fontSize: '13px',
                marginBottom: '16px',
              }}>
                {dir.categories.length} категорий
              </div>

              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                color: dir.color,
                fontSize: '14px',
                fontWeight: 600,
                padding: '8px 16px',
                borderRadius: '8px',
                background: `${dir.color}15`,
              }}>
                Открыть
                <ChevronRightIcon size={14} color={dir.color} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
